import axiosInstance from '../config/axios';

const getAuthHeader = (extraHeaders = {}) => {
  const token = localStorage.getItem('authToken');
  const validToken = token && token !== 'undefined' && token !== 'null' ? token : null;
  return {
    ...extraHeaders,
    ...(!validToken ? { _skipAuthRedirect: true } : {}),
    ...(validToken ? { Authorization: `Bearer ${validToken}` } : {})
  };
};

// ──────────────────────────────────────────────
// Community APIs
// ──────────────────────────────────────────────

/**
 * Fetch all communities with optional filters
 * @param {{ search?: string, type?: string, category?: string }} params
 */
export const getCommunitiesApi = (params = {}) =>
  axiosInstance.get('/get-community', { params, headers: getAuthHeader({ _skipAuthRedirect: true }), _skipAuthRedirect: true }).then((res) => res.data);

/**
 * Fetch a single community by ID (with mandatory token if using protected route)
 * @param {string} id
 */
export const getCommunityByIdApi = (id) =>
  axiosInstance.get(`/community/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Fetch a single community by ID for website without token requirement (GET /get-community/:id)
 * @param {string} id
 */
export const getCommunityByIdWebApi = (id) =>
  axiosInstance.get(`/get-community/${id}`, { headers: getAuthHeader({ _skipAuthRedirect: true }), _skipAuthRedirect: true }).then((res) => res.data);

/**
 * Fetch community details by ID using GET /get-community-detail/:id (optional auth)
 * @param {string} id
 */
export const getCommunityDetailByIdWebApi = (id) =>
  axiosInstance.get(`/get-community-detail/${id}`, { headers: getAuthHeader({ _skipAuthRedirect: true }), _skipAuthRedirect: true }).then((res) => res.data);

/**
 * Fetch community details by ID (alias for getCommunityByIdWebApi)
 * @param {string} id
 */
export const getCommunityDetailApi = (id) =>
  axiosInstance.get(`/get-community/${id}`, { headers: getAuthHeader({ _skipAuthRedirect: true }), _skipAuthRedirect: true }).then((res) => res.data);



/**
 * Fetch all events for a community
 * @param {string} communityId
 */
export const getCommunityEventsApi = (communityId, page = 1, limit = 100) =>
  axiosInstance.get(`/get-events?community=${communityId}&page=${page}&limit=${limit}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Fetch all lessons for a community
 * @param {string} communityId
 */
export const getCommunityLessonsApi = (communityId) =>
  axiosInstance.get(`/lessons/${communityId}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Fetch all categories
 */
export const getCategoriesApi = () =>
  axiosInstance.get('/categories', { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Create a new community
 * @param {FormData} formData 
 */
export const createCommunityApi = (formData) =>
  axiosInstance.post('/add-community', formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);

/**
 * Update an existing community
 * @param {string} id 
 * @param {FormData} formData 
 */
export const updateCommunityApi = (id, formData) =>
  axiosInstance.put(`/edit-community/${id}`, formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);

/**
 * Delete a community
 * @param {string} id 
 */
export const deleteCommunityApi = (id) =>
  axiosInstance.delete(`/delete-community/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Join a community
 * @param {string} id 
 * @param {string} userId 
 * @param {object} data
 */
export const joinCommunityApi = (id, userId, data = {}) =>
  axiosInstance.patch(`/community/${id}/join/${userId}`, data, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Approve a join request (for owners)
 * @param {string} id 
 * @param {string} userId 
 */
export const approveRequestApi = (id, userId) =>
  axiosInstance.patch(`/community/${id}/requests/${userId}/approve`, {}, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * List community members
 * @param {string} id 
 * @param {string} status 'approved' or 'pending'
 */
export const getCommunityMembersApi = (id, status = 'approved') =>
  axiosInstance.get(`/community/${id}/members`, { params: { status }, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Search communities
 * @param {{ search?: string, type?: string, category?: string }} params 
 */
export const searchCommunitiesApi = (params = {}) =>
  axiosInstance.get('/search-community', { params, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Remove a member from a community
 * @param {string} communityId 
 * @param {string} userId 
 */
export const removeMembershipApi = (communityId, userId) =>
  axiosInstance.delete(`/communities/${communityId}/members/${userId}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * List pending join requests
 * @param {string} communityId 
 */
export const getPendingRequestsApi = (communityId) =>
  axiosInstance.get(`/communities/${communityId}/pending-members`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Reject a join request
 * @param {string} communityId 
 * @param {string} userId 
 */
export const rejectRequestApi = (communityId, userId) =>
  axiosInstance.delete(`/communities/${communityId}/members/${userId}/reject`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Search for members within a community
 * @param {{ communityId: string, search: string }} params 
 */
export const searchCommunityMembersApi = (params) =>
  axiosInstance.get('/search-community-member', { params, headers: getAuthHeader() }).then((res) => res.data);

/**
 * List posts within a community
 * @param {string} communityId 
 * @param {object} params Pagination/filters
 */
export const getCommunityPostsApi = (communityId, params = {}) =>
  axiosInstance.get(`/community/${communityId}/posts`, { params, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Create a post within a community
 * @param {string} communityId 
 * @param {FormData} formData 
 */
export const createCommunityPostApi = (communityId, formData) =>
  axiosInstance.post(`/community/${communityId}/posts`, formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);

/**
 * Fetch workbooks for a community
 * @param {string} communityId 
 */
export const getCommunityWorkbooksApi = (communityId) =>
  axiosInstance.get(`/community-workbook/${communityId}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * List videos for a community
 * @param {string} communityId 
 */
export const getCommunityVideosApi = (communityId) =>
  axiosInstance.get('/list-community-videos', { params: { communityId }, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Toggle community live status
 * @param {string} id 
 * @param {object} data Object containing isLive status
 */
export const communityLiveApi = (id, data) =>
  axiosInstance.patch(`/communities/${id}/live`, data, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Like/unlike a community post
 * @param {string} postId
 */
export const likeCommunityPostApi = (postId) =>
  axiosInstance.post(`/posts/${postId}/like`, {}, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Get comments of a post
 * @param {string} postId
 */
export const getPostCommentsApi = (postId) =>
  axiosInstance.get(`/posts/${postId}/comments`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Add a comment to a post
 * @param {string} postId
 * @param {object} data Object containing text comment
 */
export const addPostCommentApi = (postId, data) =>
  axiosInstance.post(`/posts/${postId}/comments`, data, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Delete a post from community
 * @param {string} postId
 */
export const deleteCommunityPostApi = (postId) =>
  axiosInstance.delete(`/posts/${postId}`, { headers: getAuthHeader() }).then((res) => res.data);

// ──────────────────────────────────────────────
// Community Recordings APIs
// ──────────────────────────────────────────────
export const getCommunityClassRecordingApi = (params = {}) =>
  axiosInstance.get('/community-recordings', { params, headers: getAuthHeader() }).then((res) => res.data);

export const addCommunityRecordingApi = (formData) =>
  axiosInstance.post('/add-community-recording', formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);

export const deleteCommunityRecordingApi = (id) =>
  axiosInstance.delete(`/community-recording/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

// ──────────────────────────────────────────────
// Community Workbook APIs
// ──────────────────────────────────────────────
export const createWorkBookApi = (formData) =>
  axiosInstance.post('/community-workbook/create', formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);

export const getCommunityWorkbooksApiNew = (params = {}) =>
  axiosInstance.get('/event-workbook', { params, headers: getAuthHeader() }).then((res) => res.data);

export const deleteWorkBookApi = (id) =>
  axiosInstance.delete(`/community-workbook/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

// ──────────────────────────────────────────────
// Community Homework APIs
// ──────────────────────────────────────────────
export const addCommunityHomeWorkApi = (formData) =>
  axiosInstance.post('/community-homework', formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);

export const getCommunityHomeworkApi = (params = {}) =>
  axiosInstance.get('/community-homework', { params, headers: getAuthHeader() }).then((res) => res.data);

export const deleteCommunityHomeWorkApi = (id) =>
  axiosInstance.delete(`/community-homework/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

// ──────────────────────────────────────────────
// Community Video APIs
// ──────────────────────────────────────────────
export const addCommunityClassVideoApi = (formData) =>
  axiosInstance.post('/add-community-video', formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);

export const deleteCommunityClassVideoApi = (id) =>
  axiosInstance.delete(`/delete-community-video/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

export const listCommunityClassesVideoApi = (params = {}) =>
  axiosInstance.get('/list-community-videos', { params, headers: getAuthHeader() }).then((res) => res.data);

// ──────────────────────────────────────────────
// Community Quiz APIs
// ──────────────────────────────────────────────
export const createCommunityQuizApi = (data) =>
  axiosInstance.post('/create-community-quiz', data, { headers: getAuthHeader() }).then((res) => res.data);

export const listCommunityClassQuizApi = (params = {}) =>
  axiosInstance.get('/list-community-quizzes', { params, headers: getAuthHeader() }).then((res) => res.data);

export const getClassesQuizByIdApi = (id) =>
  axiosInstance.get(`/community-quiz/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

export const deleteCommunityQuizApi = (id) =>
  axiosInstance.delete(`/community-quiz/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

export const addMembersApi = (communityId, userId) =>
  axiosInstance.post('/add-members', { community: communityId, user: userId }, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * List user's joined / owned communities
 */
export const listMyCommunitiesApi = () =>
  axiosInstance.get('/community/mine', { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Edit a user post by ID
 * @param {string} postId
 * @param {FormData|Object} data
 */
export const editPostApi = (postId, data) => {
  const isFormData = data instanceof FormData;
  return axiosInstance.put(`/posts/${postId}`, data, {
    headers: getAuthHeader(isFormData ? { 'Content-Type': 'multipart/form-data' } : {})
  }).then((res) => res.data);
};

/**
 * List global posts without community requirement
 * @param {object} params
 */
export const listPostsWithoutCommunityApi = (params = {}) =>
  axiosInstance.get('/posts', { params, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Delete a comment by ID
 * @param {string} commentId
 */
export const deleteCommentApi = (commentId) =>
  axiosInstance.delete(`/comments/${commentId}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Vote on a poll option
 * @param {string} postId
 * @param {{ option_index: number }} data
 */
export const votePollOptionApi = (postId, data) =>
  axiosInstance.post(`/polls/${postId}/vote`, data, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Add option to community quiz
 * @param {string} quizId
 * @param {Object} data
 */
export const addOptionToCommunityQuizApi = (quizId, data) =>
  axiosInstance.post(`/add-option-to-community-quiz/${quizId}`, data, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Update community quiz
 * @param {string} quizId
 * @param {Object} data
 */
export const updateCommunityQuizApi = (quizId, data) =>
  axiosInstance.put(`/community-quiz/${quizId}`, data, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Get answers submitted for a community quiz
 * @param {string} quizId
 */
export const getCommunityQuizAnswersApi = (quizId) =>
  axiosInstance.get(`/community-quiz-answers/${quizId}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Get community workbook detail
 * @param {string} workbookId
 */
export const getCommunityWorkbookDetailApi = (workbookId) =>
  axiosInstance.get(`/community-workbook/${workbookId}/detail`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Update community workbook
 * @param {string} workbookId
 * @param {FormData|Object} data
 */
export const updateCommunityWorkbookApi = (workbookId, data) => {
  const isFormData = data instanceof FormData;
  return axiosInstance.put(`/community-workbook/${workbookId}`, data, {
    headers: getAuthHeader(isFormData ? { 'Content-Type': 'multipart/form-data' } : {})
  }).then((res) => res.data);
};

/**
 * Delete single file from community workbook
 * @param {string} workbookId
 * @param {string} fileId
 */
export const deleteWorkbookFileApi = (workbookId, fileId) =>
  axiosInstance.delete(`/community-workbook/${workbookId}/file/${fileId}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Update community homework
 * @param {string} id
 * @param {FormData|Object} data
 */
export const updateCommunityHomeworkApi = (id, data) => {
  const isFormData = data instanceof FormData;
  return axiosInstance.put(`/community-homework/${id}`, data, {
    headers: getAuthHeader(isFormData ? { 'Content-Type': 'multipart/form-data' } : {})
  }).then((res) => res.data);
};

/**
 * Update community video
 * @param {string} id
 * @param {Object} data
 */
export const updateCommunityVideoApi = (id, data) =>
  axiosInstance.put(`/update-community-video/${id}`, data, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Update community recording
 * @param {string} id
 * @param {Object} data
 */
export const updateCommunityRecordingApi = (id, data) =>
  axiosInstance.put(`/community-recording/${id}`, data, { headers: getAuthHeader() }).then((res) => res.data);

// ──────────────────────────────────────────────
// Categories Management APIs
// ──────────────────────────────────────────────

export const createCategoryApi = (data) =>
  axiosInstance.post('/categories', data, { headers: getAuthHeader() }).then((res) => res.data);

export const updateCategoryApi = (categoryId, data) =>
  axiosInstance.put(`/categories/${categoryId}`, data, { headers: getAuthHeader() }).then((res) => res.data);

export const deleteCategoryApi = (categoryId) =>
  axiosInstance.delete(`/categories/${categoryId}`, { headers: getAuthHeader() }).then((res) => res.data);

export const createCourseCategoryApi = (data) =>
  axiosInstance.post('/course-categories', data, { headers: getAuthHeader() }).then((res) => res.data);

export const getCourseCategoryApi = () =>
  axiosInstance.get('/course-categories', { headers: getAuthHeader() }).then((res) => res.data);

export const updateCourseCategoryApi = (categoryId, data) =>
  axiosInstance.put(`/course-categories/${categoryId}`, data, { headers: getAuthHeader() }).then((res) => res.data);

export const deleteCourseCategoryApi = (categoryId) =>
  axiosInstance.delete(`/course-categories/${categoryId}`, { headers: getAuthHeader() }).then((res) => res.data);

